import get from 'lodash/get';
import join from 'lodash/join';
import uniq from 'lodash/uniq';
import { useCallback } from 'react';
import type { Map as YMap, Doc as YDoc } from 'yjs';

import { useObservedProperty } from './yjs.client';
import { getRootMap } from '../common/utils/yjs';

export function useWidgetCustomCSSClasses(widgetId: string, document: YDoc) {
  const rootMap = getRootMap(document);

  const selector = useCallback(
    (data: YMap<string[]>) => {
      return data.get(widgetId);
    },
    [widgetId]
  );

  const [widgetCustomCSSClasses] = useObservedProperty<string[] | undefined>(rootMap, 'widgetsCustomCSSClasses', {
    deepObserve: true,
    selector
  });

  return widgetCustomCSSClasses;
}

export function useJoinedWidgetCustomCSSClasses(widgetId: string, document: YDoc) {
  return join(useWidgetCustomCSSClasses(widgetId, document), ' ');
}

export function useStaticJoinedWidgetCustomCSSClasses(widgetId: string, document: Record<string, unknown>) {
  return join(get(document, `staticContent.widgetsCustomCSSClasses.${widgetId}`) as string[], ' ');
}

export const getUniqueClassesListFromJSON = (allCustomCSSClassesJSON: Record<string, string[]>) => {
  return Array.from(new Set(Object.values(allCustomCSSClassesJSON).flat()));
};

export const getNewCustomClasses = (newClasses: string, existingClasses: string[]) => {
  const trimmed = newClasses.trim();
  const splitByWhitespace = trimmed ? trimmed.split(/\s+/g) : [];
  return uniq([...existingClasses, ...splitByWhitespace]);
};

export const useAllCustomCSSClasses = (document: YDoc) => {
  const rootMap = getRootMap(document);
  const [allCustomCSSClasses] = useObservedProperty<YMap<string[]>>(rootMap, 'widgetsCustomCSSClasses', {
    deepObserve: true
  });
  return allCustomCSSClasses;
};
